import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Mail, Pencil, Trash2 } from 'lucide-react';
import EmailTemplatePopup from './Components/EmailTemplatePopup';
import SmileCompareSlider from './Components/SmileCompareSlider';
import { createNote, getUserNotes, updateNote, deleteNote, getSmilePreviewImages, updateUserStatus } from '../Services/APIs/dashboard';
import { toImageDataUrl } from '../Utils/imageData';

const STATUS_COLORS = {
    'New': 'bg-[#C3D8CC] text-[#2F4437]',
    'Contacted': 'bg-[#92A395] text-[#2F3A32]',
    'Consultation Scheduled': 'bg-[#58665A] text-[#D0D9D2]',
};

const SELECT_CLASSES =
    'w-full px-3 py-2 border border-[#E8E6E3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#38433A] text-sm bg-white text-[#2D2D2D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const Badge = ({ label, className }) => (
    <span className={`inline-flex items-center h-5 px-2 rounded-lg font-dashboard text-[11px] uppercase tracking-[0.3px] leading-none whitespace-nowrap ${className}`}>
        {label}
    </span>
);

const InfoCard = ({ label, children }) => (
    <div className="bg-white rounded-xl border border-[#E8E6E3] p-3.5 md:p-[18px] shadow-sm">
        <p className="font-dashboard text-[9px] tracking-[0.6px] uppercase text-[#6B6B6B] mb-2">{label}</p>
        {children}
    </div>
);

const PatientDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const routePatient = location.state?.patient;
    const initialUid = routePatient?.uids?.[0] ?? null;

    const [showEmailPopup, setShowEmailPopup] = useState(false);
    const [selectedUid, setSelectedUid] = useState(initialUid);
    const [patientData, setPatientData] = useState(null);

    // Initialize patientData from route state
    useEffect(() => {
        if (!routePatient) return;
        setPatientData({
            _id: routePatient._id,
            uid: routePatient.uid,
            name: routePatient.patient || 'Patient',
            email: routePatient.email || '',
            phone: routePatient.phone || '',
            date: routePatient.date || new Date().toLocaleDateString(),
            statuses: getStatusInfo(routePatient.statuses || []),
            uids: routePatient.uids || [],
            uidCount: routePatient.uidCount || 0
        });
    }, [routePatient]);

    // Initialize selectedUid to first UID when patientData changes
    useEffect(() => {
        if (patientData?.uids && patientData.uids.length > 0) {
            setSelectedUid(patientData.uids[0]);
        }
    }, [patientData?.uids]);

    // Helper function to build statuses array from API response
    const buildStatuses = (patient) => {
        const statuses = [];

        if (patient.status === 'new') {
            statuses.push('New');
        } else if (patient.status === 'contacted') {
            statuses.push('Contacted');
        } else if (patient.status === 'consultation_scheduled') {
            statuses.push('Consultation Scheduled');
        }

        if (patient.is_high_intent) {
            statuses.push('High Intent');
        }

        return statuses;
    };

    // Smile preview images state
    const [beforeImage, setBeforeImage] = useState(null);
    const [afterImage, setAfterImage] = useState(null);
    const [loadingImages, setLoadingImages] = useState(Boolean(initialUid));
    const [imagesError, setImagesError] = useState(null);

    // Notes state
    const [notesList, setNotesList] = useState([]);
    const [newNoteText, setNewNoteText] = useState('');
    const [loadingNotes, setLoadingNotes] = useState(Boolean(initialUid));
    const [notesError, setNotesError] = useState(null);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editText, setEditText] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState(null);

    // Status update state
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [statusError, setStatusError] = useState(null);
    const [updatedPatientData, setUpdatedPatientData] = useState(null);

    // Fetch smile preview images
    useEffect(() => {
        const fetchImages = async () => {
            if (!selectedUid) return;

            setLoadingImages(true);
            setImagesError(null);
            setBeforeImage(null);
            setAfterImage(null);
            try {
                const response = await getSmilePreviewImages(selectedUid);
                if (response.data) {
                    // cropData is before, correctionData is after
                    // setBeforeImage(response.data.cropData);
                    // setAfterImage(response.data.correctionData);
                    setBeforeImage(toImageDataUrl(response.data.cropData));
                    setAfterImage(toImageDataUrl(response.data.correctionData));

                    // Update patientData from API response
                    if (response.data.patient) {
                        const apiPatient = response.data.patient;
                        setPatientData(prevData => ({
                            _id: apiPatient._id,
                            uid: apiPatient.patient_UUID || prevData.uid,
                            name: `${apiPatient.first_name || ''} ${apiPatient.last_name || ''}`.trim() || prevData.name,
                            email: apiPatient.email || prevData.email,
                            phone: apiPatient.phone || prevData.phone,
                            date: apiPatient.createdAt ? new Date(apiPatient.createdAt).toLocaleDateString() : "",
                            statuses: getStatusInfo(buildStatuses(apiPatient)),
                            uids: prevData.uids,
                            uidCount: prevData.uidCount
                        }));
                    }
                }
            } catch (err) {
                console.error('Error fetching smile preview images:', err);

                setImagesError(err?.response?.data?.message || err.message || 'Failed to load smile preview images');
            } finally {
                setLoadingImages(false);
            }
        };

        fetchImages();
    }, [selectedUid, navigate]);

    // Fetch notes on component mount
    useEffect(() => {
        const fetchNotes = async () => {
            if (!selectedUid) return;

            setLoadingNotes(true);
            setNotesError(null);
            setNotesList([]);
            try {
                const response = await getUserNotes(selectedUid);
                setNotesList(response.data || []);
            } catch (err) {
                console.error('Error fetching notes:', err);

                setNotesError(err?.response?.data?.message || err.message || 'Failed to load notes');
            } finally {
                setLoadingNotes(false);
            }
        };

        fetchNotes();
    }, [selectedUid, navigate]);

    // Handle create new note
    const handleAddNote = async () => {
        if (!newNoteText.trim()) {
            setNotesError('Note text is required');
            return;
        }

        if (newNoteText.length > 5000) {
            setNotesError('Note must not exceed 5000 characters');
            return;
        }

        setSavingNote(true);
        setNotesError(null);
        try {
            const response = await createNote({
                text: newNoteText,
                uid: patientData.uid,
            });
            setNotesList([response.data, ...notesList]);
            setNewNoteText('');
        } catch (err) {
            console.error('Error creating note:', err);

            setNotesError(err?.response?.data?.message || err.message || 'Failed to create note');
        } finally {
            setSavingNote(false);
        }
    };

    // Handle update note
    const handleUpdateNote = async (noteId) => {
        if (!editText.trim()) {
            setNotesError('Note text is required');
            return;
        }

        if (editText.length > 5000) {
            setNotesError('Note must not exceed 5000 characters');
            return;
        }

        setSavingNote(true);
        setNotesError(null);
        try {
            const response = await updateNote(noteId, {
                text: editText,
            });
            setNotesList(notesList.map(note => note._id === noteId ? response.data : note));
            setEditingNoteId(null);
            setEditText('');
        } catch (err) {
            console.error('Error updating note:', err);

            setNotesError(err?.response?.data?.message || err.message || 'Failed to update note');
        } finally {
            setSavingNote(false);
        }
    };

    // Handle delete note
    const handleDeleteNote = async (noteId) => {
        setDeletingNoteId(noteId);
        setNotesError(null);
        try {
            await deleteNote(noteId);
            setNotesList(notesList.filter(note => note._id !== noteId));
        } catch (err) {
            console.error('Error deleting note:', err);

            setNotesError(err?.response?.data?.message || err.message || 'Failed to delete note');
        } finally {
            setDeletingNoteId(null);
        }
    };

    // Handle status change
    const handleStatusChange = async (newStatus) => {
        setSelectedStatus(newStatus);
        setStatusError(null);
        setUpdatingStatus(true);

        try {
            const statusMap = {
                'new': 'new',
                'contacted': 'contacted',
                'consultation_scheduled': 'consultation_scheduled',
            };

            const apiStatus = statusMap[newStatus];

            await updateUserStatus(patientData.uid, apiStatus);
            const updatedStatuses = buildStatuses({ status: apiStatus, is_high_intent: patientData.is_high_intent })

            setPatientData(prevState => ({...prevState, statuses: getStatusInfo(updatedStatuses)}))

            setSelectedStatus(null); // Clear selection after successful update
        } catch (err) {
            console.error('Error updating status:', err);

            setStatusError(err?.response?.data?.message || err.message || 'Failed to update status');
            // Reset the select to previous value on error
            setSelectedStatus(null);
        } finally {
            setUpdatingStatus(false);
        }
    };

    // Extract status display info
    const getStatusInfo = (data) => {

        const intent = data.includes('High Intent') ? 'High Intent' : '';
        const status = data.find(s => s !== 'High Intent') || data[0];
        return { status, intent };
    };


    // Handle UID change
    const handleUidChange = (newUid) => {
        setSelectedUid(newUid);
    };

    if (!patientData) {
        return (
            <main className="flex-1 overflow-auto">
                <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto text-center text-[#6B6B6B]">
                    Redirecting...
                </div>
            </main>
        );
    }

    // Loading / error / no-images placeholder shared by both image panes. The comparison needs
    // both halves, so one missing image is treated the same as none — a lone pane with nothing
    // to compare against is worse than saying the simulation isn't there.
    const imagePlaceholder = loadingImages ? (
        <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#38433A] mx-auto mb-2" />
            <p className="text-sm text-gray-600">Loading images...</p>
        </div>
    ) : imagesError ? (
        <div className="rounded-xl bg-red-50 py-16 text-center">
            <p className="text-sm text-red-600">{imagesError}</p>
        </div>
    ) : !beforeImage || !afterImage ? (
        <div className="rounded-xl bg-[#FAFAF9] border border-[#E8E6E3] py-16 px-6 text-center">
            <AlertCircle className="w-6 h-6 text-[#ADADAD] mx-auto mb-2.5" />
            <p className="text-sm text-[#2D2D2D]">No smile preview available for this case</p>
            <p className="text-xs text-[#6B6B6B] mt-1">
                The simulation images haven't been generated or couldn't be read.
            </p>
        </div>
    ) : null;

    return (
        <main className="flex-1 overflow-auto">
            <div className="mx-auto w-full max-w-7xl flex flex-col gap-4 md:gap-5 px-4 md:px-8 pt-4 md:pt-6 pb-6">
                <div>
                    <button
                        onClick={() => navigate('/dashboard/patients')}
                        className="flex items-center gap-1.5 text-[#6B6B6B] hover:text-[#2D2D2D] mb-2.5 transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span className="font-dashboard text-[10px] uppercase tracking-[0.6px]">Back to Patients</span>
                    </button>
                    <h1 className="text-2xl md:text-[32px] text-[#2D2D2D] tracking-tight">Patient Details</h1>
                </div>

                <div className={`grid grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-4 ${patientData?.uidCount > 1 ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}>
                    <InfoCard label="Patient Name">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[14px] text-[#2D2D2D]">{patientData?.name || 'Patient'}</span>
                            {patientData.statuses?.intent && (
                                <Badge label="High Intent" className="bg-[#D3FDD9] text-[#2F5E38]" />
                            )}
                        </div>
                    </InfoCard>

                    <InfoCard label="Email">
                        <span className="text-[13px] text-[#2D2D2D] break-all">{patientData?.email || 'N/A'}</span>
                    </InfoCard>

                    <InfoCard label="Status">
                        <Badge
                            label={patientData?.statuses?.status || 'Unknown'}
                            className={STATUS_COLORS[patientData?.statuses?.status] || 'bg-[#C3D8CC] text-[#2F4437]'}
                        />
                    </InfoCard>

                    <InfoCard label="Submission Date">
                        <span className="text-[14px] text-[#2D2D2D]">{patientData?.date || 'N/A'}</span>
                    </InfoCard>

                    <InfoCard label="Patient Status">
                        <select
                            value={selectedStatus || ''}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            disabled={updatingStatus}
                            className={SELECT_CLASSES}
                        >
                            <option value="">Select status...</option>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="consultation_scheduled">Consultation Scheduled</option>
                        </select>
                        {statusError && (
                            <p className="text-xs text-red-600 mt-2">{statusError}</p>
                        )}
                        {updatingStatus && (
                            <p className="text-xs text-[#6B6B6B] mt-2 flex items-center gap-1">
                                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#38433A]" />
                                Updating...
                            </p>
                        )}
                    </InfoCard>

                    {patientData?.uidCount > 1 && (
                        <InfoCard label="Case Selection">
                            <select
                                value={selectedUid || ''}
                                onChange={(e) => handleUidChange(e.target.value)}
                                className={SELECT_CLASSES}
                            >
                                {patientData.uids.map((uid, index) => (
                                    <option key={uid} value={uid}>
                                        Case {index + 1}: {uid}
                                    </option>
                                ))}
                            </select>
                        </InfoCard>
                    )}
                </div>
                <div className="bg-white rounded-2xl border border-[#E8E6E3] shadow-sm overflow-hidden">
                    <div className="border-b border-[#E8E6E3] px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                        <h2 className="text-[18px] text-[#2D2D2D]">Smile Preview Simulation</h2>
                        {/* Icon-only below sm — the label is the first thing worth dropping when
                          * the header has to share a phone-width row with the section title. */}
                        <button
                            onClick={() => setShowEmailPopup(true)}
                            aria-label="Message patient"
                            title="Message"
                            className="font-dashboard shrink-0 inline-flex items-center justify-center gap-1.5 h-10 w-10 sm:h-9 sm:w-auto sm:px-3.5 bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors text-[10px] uppercase tracking-[0.5px]"
                        >
                            <Mail className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline">Message</span>
                        </button>
                    </div>

                    <div className="p-4 md:p-6">
                        {imagePlaceholder || (
                            /* Stacked on phones; the pair and the slider sit side by side from md up. */
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="grid grid-cols-2 gap-2.5">
                                    {[
                                        { src: beforeImage, label: 'Before' },
                                        { src: afterImage, label: 'After' },
                                    ].map((img) => (
                                        /* object-contain, not cover: these are the result being
                                           judged, so no part of either image may be cropped away. */
                                        <div key={img.label} className="relative rounded-xl overflow-hidden bg-[#F5F3F0] aspect-[3/4]">
                                            <img src={img.src} alt={img.label} className="w-full h-full object-contain" />
                                            <div className="absolute top-2.5 left-2.5 bg-black/60 text-white px-2.5 py-1 rounded-md">
                                                <span className="font-dashboard text-[10px] uppercase tracking-[0.5px]">{img.label}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <SmileCompareSlider beforeSrc={beforeImage} afterSrc={afterImage} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-[#E8E6E3]">
                    <h2 className="text-[18px] md:text-xl text-[#2D2D2D] mb-4 md:mb-6">Internal Notes</h2>

                    {notesError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <p className="text-sm text-red-800">{notesError}</p>
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Previous Notes */}
                        <div>
                            <h3 className="text-sm text-[#6B6B6B] tracking-wider mb-3 font-dashboard">PREVIOUS NOTES</h3>
                            {loadingNotes ? (
                                <div className="p-4 text-center text-[#6B6B6B]">Loading notes...</div>
                            ) : notesList.length === 0 ? (
                                <div className="p-4 bg-[#F5F3F0] rounded-lg text-[#6B6B6B] text-sm">No notes yet</div>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {notesList.map((note) => (
                                        <div key={note._id} className="bg-[#F5F3F0] border border-[#E8E6E3] rounded-lg p-4">
                                            {editingNoteId === note._id ? (
                                                // Edit mode
                                                <div className="space-y-3">
                                                    <textarea
                                                        className="w-full p-3 border border-[#E8E6E3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#38433A] text-sm"
                                                        value={editText}
                                                        onChange={(e) => setEditText(e.target.value)}
                                                        maxLength="5000"
                                                        rows="4"
                                                    />
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-[#6B6B6B]">{editText.length}/5000</span>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingNoteId(null);
                                                                    setEditText('');
                                                                }}
                                                                disabled={savingNote}
                                                                className="px-3 py-1.5 text-sm bg-white text-[#6B6B6B] border border-[#E8E6E3] rounded-lg hover:bg-[#F5F3F0] transition-colors disabled:opacity-50"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleUpdateNote(note._id)}
                                                                disabled={savingNote}
                                                                className="px-3 py-1.5 text-sm bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors disabled:opacity-50 flex items-center gap-2"
                                                            >
                                                                {savingNote ? (
                                                                    <>
                                                                        <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    'Save'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // View mode
                                                <>
                                                    <p className="text-[#2D2D2D] text-sm mb-2">{note.text}</p>
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-xs text-[#6B6B6B]">
                                                            {new Date(note.createdAt).toLocaleDateString()} at {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingNoteId(note._id);
                                                                    setEditText(note.text);
                                                                }}
                                                                className="text-[#6B6B6B] hover:text-[#38433A] transition-colors p-1"
                                                                title="Edit note"
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteNote(note._id)}
                                                                disabled={deletingNoteId === note._id}
                                                                className="text-[#6B6B6B] hover:text-red-600 transition-colors p-1 disabled:opacity-50"
                                                                title="Delete note"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Add New Note */}
                        <div>
                            <h3 className="text-sm text-[#6B6B6B] tracking-wider mb-2 font-dashboard"> ADD NEW NOTE</h3>
                            <textarea
                                className="w-full resize-none rounded-xl border border-[#E8E6E3] bg-[#F5F3F0] px-3 py-2 text-sm min-h-32 md:min-h-40 mb-4 outline-none transition-colors focus:border-[#38433A]"
                                rows="4"
                                placeholder="Add notes about this patient's case..."
                                value={newNoteText}
                                onChange={(e) => setNewNoteText(e.target.value)}
                                maxLength="5000"
                            ></textarea>
                            <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-[#6B6B6B]">{newNoteText.length}/5000</span>
                                <button
                                    onClick={handleAddNote}
                                    disabled={savingNote}
                                    className="font-dashboard px-6 py-2.5 bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {savingNote ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        'SAVE NOTES'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showEmailPopup && (
                <EmailTemplatePopup
                    patientEmail={patientData.email}
                    patientData={patientData}
                    onClose={() => setShowEmailPopup(false)}
                />
            )}

        </main>
    );
};

export default PatientDetails;